/**
 * React Query Hooks for Tools
 *
 * Query hooks for tool discovery
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { toolsApi } from '../api';
import type { ToolsResponse, MCPToolsResponse } from '@/types';

export const TOOLS_QUERY_KEYS = {
  tools: ['tools'] as const,
  available: () => [...TOOLS_QUERY_KEYS.tools, 'available'] as const,
  mcpTools: () => [...TOOLS_QUERY_KEYS.tools, 'mcp'] as const,
};

/**
 * Get all available tools (builtin + custom)
 */
export function useToolsQuery(): UseQueryResult<ToolsResponse, Error> {
  return useQuery({
    queryKey: TOOLS_QUERY_KEYS.available(),
    queryFn: () => toolsApi.fetchAvailableTools(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get all available MCP tools
 */
export function useMCPToolsQuery(
  forceReconnect = false,
  enabled = true
): UseQueryResult<MCPToolsResponse, Error> {
  return useQuery({
    queryKey: [...TOOLS_QUERY_KEYS.mcpTools(), forceReconnect],
    queryFn: () => toolsApi.fetchAvailableMCPServers(forceReconnect),
    enabled,
    staleTime: 1000 * 60, // 1 minute
    retry: false, // Don't retry on MCP connection failures
  });
}
